import React, { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, Save } from "lucide-react";
import { GeneratorSettings } from "@/components/mansioni/GeneratorSettings";
import { GeneratorPreview } from "@/components/mansioni/GeneratorPreview";

const err = (e) => toast.error(e.response?.data?.detail || "Errore");

export default function GeneraTurni() {
  const qc = useQueryClient();
  const nav = useNavigate();
  const [params, setParams] = useState(null);
  const [plan, setPlan] = useState(null);
  const { data: mansioni = [] } = useQuery({
    queryKey: ["mansioni-active"],
    queryFn: async () => (await api.get("/mansioni-area/mansioni", { params: { only_active: true } })).data,
  });

  const previewMut = useMutation({
    mutationFn: async (p) => (await api.post("/mansioni-area/generator/preview", p)).data,
    onSuccess: (d, p) => { setParams(p); setPlan(d); }, onError: err,
  });
  const commitMut = useMutation({
    mutationFn: async () => (await api.post("/mansioni-area/generator/commit", { ...params, items: plan.items })).data,
    onSuccess: (r) => {
      toast.success(`${r.created} turni salvati${r.deleted ? ` (${r.deleted} precedenti sostituiti)` : ""}`);
      qc.invalidateQueries({ queryKey: ["turni"] });
      nav("/mansioni/turni");
    },
    onError: err,
  });

  const updateItems = (fn) => setPlan((p) => ({ ...p, items: fn(p.items) }));
  const counts = useMemo(() => {
    const c = {};
    (plan?.items || []).forEach((i) => { c[i.student_id] = (c[i.student_id] || 0) + 1; });
    return c;
  }, [plan]);

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-[1500px] mx-auto" data-testid="genera-turni-page">
      <Link to="/mansioni/turni" className="text-sm text-stone-500 inline-flex items-center gap-1 mb-3 hover:text-stone-800"><ArrowLeft size={14}/> Turni & Mansioni</Link>
      <PageHeader
        title="Genera turni automaticamente"
        subtitle="Rotazione e carico equi in base allo storico dell'ultimo mese: rispetta risorse, giorni, esclusioni e impegni esterni (con sostituto solo nei giorni coinvolti)."
        actions={plan && (
          <Button onClick={() => window.confirm(`Salvare ${plan.items.length} turni?${params.replace_existing && plan.existing_to_replace ? ` Verranno sostituiti ${plan.existing_to_replace} turni già presenti per queste mansioni nel periodo.` : ""}`) && commitMut.mutate()}
            disabled={commitMut.isPending || !plan.items.length} className="rounded-full bg-[#1C352D] hover:bg-[#264A3E]" data-testid="gen-commit">
            <Save size={14} className="mr-1.5"/> {commitMut.isPending ? "Salvataggio…" : `Conferma e salva (${plan.items.length})`}
          </Button>
        )}
      />
      <GeneratorSettings mansioni={mansioni} loading={previewMut.isPending} onGenerate={(p) => previewMut.mutate(p)}/>
      {plan && <GeneratorPreview plan={plan} params={params} counts={counts} updateItems={updateItems}/>}
    </div>
  );
}
